import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Building, Search, MapPin, Globe, Users, Briefcase, Link2, Unlink,
  Plus, Sparkles, Trash2, ExternalLink, ChevronRight, ArrowRight,
  Megaphone, Target, Star, CheckCircle, Loader2, X, FileText, Wand2,
  Crown, Mail, Shield
} from 'lucide-react';

// ==================== BUSINESS CARD ====================
const BusinessCard = ({ biz, user, onLink, onUnlink, onClaim, isLinked }) => {
  const isOwned = !!biz.owner_id;
  const isMyBiz = user?.owned_business_id === biz.id;

  return (
    <Card className={`hover:shadow-md transition-shadow ${isMyBiz ? 'border-amber-300 bg-amber-50/30' : ''}`} data-testid={`business-card-${biz.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border">
            {biz.logo_url ? (
              <img src={resolveImageUrl(biz.logo_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <Building className="w-5 h-5 text-blue-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-semibold text-sm truncate">{biz.business_name}</p>
              {isMyBiz && <Badge className="text-[8px] px-1 py-0 bg-amber-100 text-amber-800 border-amber-200">Yours</Badge>}
              {isOwned && !isMyBiz && <Badge variant="outline" className="text-[8px] px-1 py-0">Claimed</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate">{biz.full_name}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {biz.city && (
                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5" />{biz.city}, {biz.state}
                </span>
              )}
              {biz.is_hiring && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-green-300 text-green-700 bg-green-50">Hiring</Badge>
              )}
            </div>
            {biz.business_description && (
              <p className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2">{biz.business_description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          {user?.account_type !== 'business' && (
            <>
              {/* Link / Unlink (employment) */}
              {isLinked ? (
                <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => onUnlink()} data-testid={`unlink-btn-${biz.id}`}>
                  <Unlink className="w-3 h-3 mr-1" />Unlink
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => onLink(biz.id)} data-testid={`link-btn-${biz.id}`}>
                  <Link2 className="w-3 h-3 mr-1" />I Work Here
                </Button>
              )}
              {/* Claim ownership */}
              {!isOwned && !user?.owned_business_id && (
                <Button size="sm" className="flex-1 text-xs h-8 bg-amber-600 hover:bg-amber-700" onClick={() => onClaim(biz.id)} data-testid={`claim-btn-${biz.id}`}>
                  <Crown className="w-3 h-3 mr-1" />Claim
                </Button>
              )}
            </>
          )}
          {biz.website && (
            <Button size="sm" variant="ghost" className="text-xs h-8 px-2" asChild>
              <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer">
                <Globe className="w-3 h-3" />
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== RECRUITING AD CARD ====================
const RecruitingAdCard = ({ ad, isOwner, onDelete }) => (
  <Card className="hover:shadow-md transition-shadow border-l-4 border-l-blue-500" data-testid={`recruiting-ad-${ad.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            {ad.logo_url ? (
              <img src={resolveImageUrl(ad.logo_url)} alt="" className="w-full h-full rounded-lg object-cover" />
            ) : (
              <Megaphone className="w-4 h-4 text-blue-600" />
            )}
          </div>
          <div>
            <p className="font-semibold text-sm">{ad.title}</p>
            <p className="text-xs text-muted-foreground">{ad.business_name}</p>
            {ad.city && <p className="text-[10px] text-muted-foreground flex items-center gap-0.5 mt-0.5"><MapPin className="w-2.5 h-2.5" />{ad.city}, {ad.state}</p>}
          </div>
        </div>
        {ad.is_featured && <Badge className="text-[9px] bg-amber-100 text-amber-800 border-amber-200">Featured</Badge>}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{ad.description}</p>
      <div className="flex items-center gap-2 mt-3">
        {ad.cta_url && (
          <Button size="sm" className="text-xs h-7" asChild>
            <a href={ad.cta_url.startsWith('http') ? ad.cta_url : `https://${ad.cta_url}`} target="_blank" rel="noopener noreferrer">
              {ad.cta_text || 'Apply Now'} <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </Button>
        )}
        {isOwner && (
          <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500 hover:text-red-600" onClick={() => onDelete(ad.id)} data-testid={`delete-ad-${ad.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

// ==================== AD CREATION STUDIO ====================
const AdCreationStudio = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', cta_text: 'Apply Now', cta_url: '', style: 'professional' });

  const handleAiAssist = async () => {
    setAiLoading(true);
    try {
      const res = await api.aiAssistAd();
      if (res.suggestion) {
        setForm(f => ({
          ...f,
          title: res.suggestion.title || f.title,
          description: res.suggestion.description || f.description,
          cta_text: res.suggestion.cta_text || f.cta_text,
        }));
        toast.success('AI suggestion applied!');
      }
    } catch { toast.error('AI assist failed'); }
    setAiLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.description) return toast.error('Title and description are required');
    setLoading(true);
    try {
      await api.createRecruitingAd(form);
      toast.success('Recruiting ad created!');
      setForm({ title: '', description: '', cta_text: 'Apply Now', cta_url: '', style: 'professional' });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ad');
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} className="w-full" data-testid="create-ad-btn">
        <Plus className="w-4 h-4 mr-1.5" />Create Recruiting Ad
      </Button>
    );
  }

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="ad-creation-studio">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">
            <Megaphone className="w-4 h-4 text-blue-600" />Ad Creation Studio
          </h3>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setOpen(false)}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>

        <Button size="sm" variant="outline" className="w-full text-xs" onClick={handleAiAssist} disabled={aiLoading} data-testid="ai-assist-btn">
          {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
          {aiLoading ? 'Generating...' : 'AI Generate Ad Copy'}
        </Button>

        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Job Title / Headline *</label>
          <Input placeholder="e.g. Senior Real Estate Agent Wanted" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="text-sm" data-testid="ad-title-input" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Description *</label>
          <textarea
            placeholder="Describe the position, requirements, and what makes your company great..."
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px] resize-none"
            data-testid="ad-description-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">CTA Button Text</label>
            <Input placeholder="Apply Now" value={form.cta_text} onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))} className="text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Application URL</label>
            <Input placeholder="https://..." value={form.cta_url} onChange={e => setForm(f => ({ ...f, cta_url: e.target.value }))} className="text-sm" data-testid="ad-cta-url-input" />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Ad Style</label>
          <div className="flex gap-2">
            {['professional', 'modern', 'bold'].map(s => (
              <button
                key={s}
                onClick={() => setForm(f => ({ ...f, style: s }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all capitalize ${form.style === s ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'}`}
                data-testid={`ad-style-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" className="flex-1 text-xs" onClick={handleSubmit} disabled={loading} data-testid="submit-ad-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" />Post Ad ($100/mo)</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== PARTNER SUGGESTION CARD ====================
const PartnerSuggestionCard = ({ partner }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-border/50 last:border-0" data-testid={`partner-suggestion-${partner.id}`}>
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400/20 to-purple-400/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
      {partner.logo_url ? (
        <img src={resolveImageUrl(partner.logo_url)} alt="" className="w-full h-full object-cover" />
      ) : (
        <Building className="w-4 h-4 text-indigo-500" />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold truncate">{partner.business_name}</p>
      <p className="text-[10px] text-muted-foreground">{partner.city}, {partner.state}</p>
    </div>
    {partner.is_hiring && <Badge variant="outline" className="text-[8px] px-1 py-0 border-green-300 text-green-700">Hiring</Badge>}
  </div>
);

// ==================== MAIN PAGE ====================
const TABS = [
  { id: 'directory', label: 'Business Directory', icon: Building },
  { id: 'recruiting', label: 'Job Board', icon: Megaphone },
  { id: 'my-ads', label: 'My Ads', icon: FileText, businessOnly: true },
];

export const BusinessMarketplace = () => {
  const { user, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('directory');
  const [businesses, setBusinesses] = useState([]);
  const [partners, setPartners] = useState([]);
  const [featuredAds, setFeaturedAds] = useState([]);
  const [regularAds, setRegularAds] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [ownershipInvites, setOwnershipInvites] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dirRes, partRes, adsRes, invitesRes] = await Promise.all([
        api.getBusinessDirectory(),
        api.getBusinessPartners(),
        api.getRecruitingAdsFeed(),
        api.getOwnershipInvites().catch(() => ({ invites: [] })),
      ]);
      setBusinesses(dirRes.businesses || []);
      setPartners(partRes.partners || []);
      setFeaturedAds(adsRes.featured || []);
      setRegularAds(adsRes.regular || []);
      setOwnershipInvites(invitesRes.invites || []);
    } catch {}
    setLoading(false);
  }, []);

  const fetchMyAds = useCallback(async () => {
    if (user?.account_type !== 'business') return;
    try {
      const res = await api.getMyRecruitingAds();
      setMyAds(res.ads || []);
    } catch {}
  }, [user?.account_type]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === 'my-ads') fetchMyAds(); }, [activeTab, fetchMyAds]);

  const handleLink = async (bizId) => {
    try {
      const res = await api.linkToBusiness(bizId);
      toast.success(res.message || 'Linked to business!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    }
  };

  const handleUnlink = async () => {
    try {
      const res = await api.unlinkFromBusiness();
      toast.success(res.message || 'Unlinked from business');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    }
  };

  const handleDeleteAd = async (adId) => {
    try {
      await api.deleteRecruitingAd(adId);
      toast.success('Ad deleted');
      fetchMyAds();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleClaim = async (bizId) => {
    try {
      const res = await api.claimBusiness(bizId);
      toast.success(res.message || 'Business claimed!');
      await refreshProfile();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim');
    }
  };

  const handleReleaseOwnership = async () => {
    try {
      const res = await api.releaseOwnership();
      toast.success(res.message || 'Ownership released');
      await refreshProfile();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const res = await api.acceptOwnership(inviteId);
      toast.success(res.message || 'Ownership accepted!');
      await refreshProfile();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await api.declineOwnership(inviteId);
      toast.success('Invite declined');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
  };

  const filteredBiz = businesses.filter(b => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (b.business_name || '').toLowerCase().includes(s) ||
           (b.city || '').toLowerCase().includes(s) ||
           (b.full_name || '').toLowerCase().includes(s) ||
           (b.business_industry || '').toLowerCase().includes(s);
  });

  const visibleTabs = TABS.filter(t => !t.businessOnly || user?.account_type === 'business');

  return (
    <Layout>
      <div className="space-y-6" data-testid="business-marketplace">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Business Marketplace</h1>
          <p className="text-sm text-muted-foreground mt-1">Discover businesses, find jobs, and connect with partners</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/50 rounded-lg p-1" data-testid="marketplace-tabs">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          {/* Main Content */}
          <div className="space-y-4">
            {/* DIRECTORY TAB */}
            {activeTab === 'directory' && (
              <>
                {/* Ownership Invites Banner */}
                {ownershipInvites.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50/50" data-testid="ownership-invites">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-900">Ownership Invites</span>
                      </div>
                      {ownershipInvites.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-amber-200">
                          <div>
                            <p className="text-sm font-medium">{inv.business_name}</p>
                            <p className="text-[10px] text-muted-foreground">wants you as their owner</p>
                          </div>
                          <div className="flex gap-1.5">
                            <Button size="sm" className="text-xs h-7 bg-amber-600 hover:bg-amber-700" onClick={() => handleAcceptInvite(inv.id)} data-testid={`accept-invite-${inv.id}`}>
                              Accept
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleDeclineInvite(inv.id)} data-testid={`decline-invite-${inv.id}`}>
                              Decline
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Owned Business Banner */}
                {user?.owned_business_id && user?.account_type !== 'business' && (
                  <Card className="border-amber-200 bg-amber-50/50" data-testid="owned-business-banner">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">
                          You own: {businesses.find(b => b.id === user.owned_business_id)?.business_name || 'Your business'}
                        </span>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={handleReleaseOwnership} data-testid="release-ownership-btn">
                        <Shield className="w-3 h-3 mr-1" />Release
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Linked Business Banner */}
                {user?.linked_business_id && user?.account_type !== 'business' && (
                  <Card className="border-green-200 bg-green-50/50" data-testid="linked-business-banner">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          Linked to: {businesses.find(b => b.id === user.linked_business_id)?.business_name || 'Your business'}
                        </span>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={handleUnlink} data-testid="unlink-banner-btn">
                        <Unlink className="w-3 h-3 mr-1" />Unlink
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search businesses by name, city, or industry..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="business-search"
                  />
                </div>

                {/* Business Grid */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredBiz.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Building className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                      <p className="text-sm text-muted-foreground">
                        {search ? 'No businesses match your search' : 'No business accounts yet. Be the first to register one!'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredBiz.map(biz => (
                      <BusinessCard
                        key={biz.id}
                        biz={biz}
                        user={user}
                        isLinked={user?.linked_business_id === biz.id}
                        onLink={handleLink}
                        onUnlink={handleUnlink}
                        onClaim={handleClaim}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* JOB BOARD TAB */}
            {activeTab === 'recruiting' && (
              <div className="space-y-4">
                {featuredAds.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-amber-500" />Featured Positions
                    </h3>
                    <div className="space-y-3">
                      {featuredAds.map(ad => (
                        <RecruitingAdCard key={ad.id} ad={ad} isOwner={ad.business_id === user?.id} onDelete={handleDeleteAd} />
                      ))}
                    </div>
                  </div>
                )}
                {regularAds.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2">All Positions</h3>
                    <div className="space-y-3">
                      {regularAds.map(ad => (
                        <RecruitingAdCard key={ad.id} ad={ad} isOwner={ad.business_id === user?.id} onDelete={handleDeleteAd} />
                      ))}
                    </div>
                  </div>
                )}
                {featuredAds.length === 0 && regularAds.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Megaphone className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                      <p className="text-sm text-muted-foreground">No recruiting ads yet.</p>
                      {user?.account_type === 'business' && (
                        <p className="text-xs text-muted-foreground mt-1">Switch to the "My Ads" tab to create your first recruiting ad!</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* MY ADS TAB (business only) */}
            {activeTab === 'my-ads' && user?.account_type === 'business' && (
              <div className="space-y-4">
                <AdCreationStudio onCreated={() => { fetchMyAds(); fetchData(); }} />

                {myAds.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <FileText className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                      <p className="text-sm text-muted-foreground">You haven't created any recruiting ads yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">Use the button above to create your first ad!</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold">Your Recruiting Ads ({myAds.length})</p>
                    {myAds.map(ad => (
                      <RecruitingAdCard key={ad.id} ad={ad} isOwner onDelete={handleDeleteAd} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Business Partner Suggestions */}
            {partners.length > 0 && (
              <Card data-testid="partner-suggestions">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-indigo-500" />
                    <p className="text-sm font-semibold">Suggested Partners</p>
                  </div>
                  <div className="space-y-0">
                    {partners.slice(0, 5).map(p => (
                      <PartnerSuggestionCard key={p.id} partner={p} />
                    ))}
                  </div>
                  {partners.length > 5 && (
                    <Button variant="ghost" size="sm" className="w-full text-xs mt-2" onClick={() => setActiveTab('directory')}>
                      View All <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Stats */}
            <Card data-testid="marketplace-stats">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm font-semibold">Marketplace Stats</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-muted/50 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold">{businesses.length}</p>
                    <p className="text-[10px] text-muted-foreground">Businesses</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold">{featuredAds.length + regularAds.length}</p>
                    <p className="text-[10px] text-muted-foreground">Open Positions</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA for non-business users */}
            {user?.account_type !== 'business' && (
              <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="register-business-cta">
                <CardContent className="p-4 text-center">
                  <Building className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm font-semibold text-blue-900">Own a Business?</p>
                  <p className="text-[11px] text-blue-700 mt-1">Register as a business account to post recruiting ads and connect with professionals.</p>
                  <Link to="/register">
                    <Button size="sm" className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-xs" data-testid="register-business-btn">
                      Register Business <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BusinessMarketplace;
